import { useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Checkbox } from '@verlo/shared/components/ui/checkbox';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { Utensils, Plus, Minus } from 'lucide-react';

const ModifierSelectDialog = ({
  open,
  onOpenChange,
  item,
  onConfirm,
  formatCurrency,
  globalModifiers = [],
}) => {
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [quantity, setQuantity] = useState(1);

  // Get available modifiers - combine item-specific and global modifiers
  const availableModifiers = [
    ...(item?.modifiers || []).map(mod => 
      typeof mod === 'string' ? { name: mod, price: 0 } : mod
    ),
    ...globalModifiers.filter(gm => gm.is_active)
  ];

  // Remove duplicates by name
  const uniqueModifiers = availableModifiers.reduce((acc, mod) => {
    if (!acc.find(m => m.name === mod.name)) {
      acc.push(mod);
    }
    return acc;
  }, []);

  const toggleModifier = (modifierName) => {
    setSelectedModifiers(prev =>
      prev.includes(modifierName)
        ? prev.filter(m => m !== modifierName)
        : [...prev, modifierName]
    );
  };

  // Calculate total with modifiers
  const modifierTotal = selectedModifiers.reduce((sum, modName) => {
    const mod = uniqueModifiers.find(m => m.name === modName);
    return sum + (mod?.price || 0);
  }, 0);
  const itemTotal = ((item?.price || 0) + modifierTotal) * quantity;

  const handleConfirm = () => {
    // Pass the priced total along so the caller's cart shows what the order will
    // actually be billed — not just the bare item price this dialog was opened with.
    onConfirm(item, selectedModifiers, quantity, modifierTotal);
    setSelectedModifiers([]);
    setQuantity(1);
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedModifiers([]);
    setQuantity(1);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="form" className="rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Utensils size={20} />
            {item?.name}
          </DialogTitle>
          <DialogDescription>
            {item?.description}
          </DialogDescription>
        </DialogHeader>
        
        {item && (
          <div className="space-y-4 py-2">
            {/* Quantity Selector */}
            <div className="flex items-center justify-between p-3 bg-surface-2">
              <span className="font-medium text-sm">Quantity</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-none"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus size={14} />
                </Button>
                <span className="font-bold text-lg w-8 text-center">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-none"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Modifiers */}
            {uniqueModifiers.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Options <span className="text-ink-faint font-normal">(optional)</span>
                </Label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {uniqueModifiers.map((mod, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 border cursor-pointer transition-colors ${
                        selectedModifiers.includes(mod.name)
                          ? 'bg-verlo/5 border-verlo'
                          : 'hover:bg-surface-2 border-hairline'
                      }`}
                      onClick={() => toggleModifier(mod.name)}
                      data-testid={`modifier-${mod.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedModifiers.includes(mod.name)}
                          className="rounded-none pointer-events-none"
                        />
                        <span className="text-sm font-medium">{mod.name}</span>
                      </div>
                      {mod.price > 0 && (
                        <span className="text-sm text-ink-soft">
                          +{formatCurrency(mod.price)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between pt-3 border-t border-hairline">
              <span className="font-medium">Total</span>
              <span className="text-xl font-bold text-verlo">
                {formatCurrency(itemTotal)}
              </span>
            </div>
          </div>
        )}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} className="rounded-none">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            className="bg-verlo hover:bg-verlo-hover rounded-none"
            data-testid="add-to-order-btn"
          >
            Add to Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModifierSelectDialog;
